import React, {useCallback} from 'react';
import {useIntl} from 'react-intl';

import {Button} from '@views/Account/Plan/_components/ActionButtons';
import {useView} from '@views/Account/Plan/_hooks/useView';
import {ModalDayPicker} from '@components/atoms';
import {useCalendarDates} from '@views/Account/Plan/_hooks/useCalendarDates';
import {useCurrentBoxId} from '@views/Account/Plan/_hooks/useCurrentBoxId';
import {formatDate} from 'ui/helpers';
import {useUpdateNextOrderDate} from '@components/_context/UserOverallInfo/_hooks';
import {useToast} from '@components/_context';
import {useNextBoxDate} from '@views/Account/_hooks';
import {useLoading} from '@views/Account/Plan/_hooks/useLoading';

const TEXT = {
  BUTTON_CHANGE_DATE: {id: 'account_yourPlan.planPanel.changeDate'},
  TOAST_SUCCESS: {id: 'account.currentLK.toast_success'},
  TOAST_ERROR: {id: 'account.currentLK.toast_error'},
};

const dateToString = (date: Date): string => formatDate({dateIn: date, format: 'YYYY-MM-DD'});

interface ActionChangeDate {
  onSuccess: () => Promise<void>;
}

export const ActionChangeDate: React.FC<ActionChangeDate> = ({onSuccess}) => {
  const {formatMessage} = useIntl();
  const {visible, open, close} = useView();
  const {showError, showSuccess} = useToast();
  const {boxId} = useCurrentBoxId();
  const {updateNextOrderDate} = useUpdateNextOrderDate(boxId);
  const {raw: boxDate = new Date()} = useNextBoxDate();
  const {loading, startLoading, stopLoading} = useLoading();

  const {payload} = useCalendarDates();
  const {minDate, maxDate, unavailableDates} = payload || {};
  const minDateString = minDate ? dateToString(minDate) : '';
  const maxDateString = maxDate ? dateToString(maxDate) : '';
  const unavailableDatesString = unavailableDates ? unavailableDates.map(dateToString) : [];

  const handleSubmit = useCallback(
    async (date: string) => {
      try {
        startLoading();
        await updateNextOrderDate(date);
        showSuccess({message: formatMessage(TEXT.TOAST_SUCCESS)});
        await onSuccess();
        close();
      } catch {
        showError({message: formatMessage(TEXT.TOAST_ERROR)});
      } finally {
        stopLoading();
      }
    },
    [updateNextOrderDate, formatMessage, showSuccess, showError, onSuccess, close, startLoading, stopLoading],
  );

  return (
    <>
      <Button onPress={open}>{formatMessage(TEXT.BUTTON_CHANGE_DATE)}</Button>
      <ModalDayPicker
        minDate={minDateString}
        maxDate={maxDateString}
        unavailableDates={unavailableDatesString}
        date={boxDate}
        onClose={close}
        isVisible={visible}
        onSubmit={handleSubmit}
        busy={loading}
      />
    </>
  );
};
